// components/CustomerTable.js
import React from 'react';
import { Pagination } from 'semantic-ui-react';
import {
  formatCallGroupMemberStatus,
  formatCallGroupPriorityCell,
  formatCallGroupRequiredCell,
  formatCallGroupNameCell,
  callGroupEscalationColor,
  formatOutcomeStatus,
} from './callGroupCustomerFormat';

const CustomerTable = ({
  customers,
  customersPage,
  customersPerPage,
  getTotalCustomers,
  viewCustomer,
  setCustomersPage,
  onOptOutChange,
  isCampaignSaved = false,
  /** When true, match CampaignDetails columns (Priority, Required, Group name, Escalation; Status → Outcome). */
  showCallGroupColumns = false,
  /** Bulk delete UI; omit to hide (CampaignForm always passes this from the bulk hook). */
  bulkSelection = null,
}) => {
  const start = (customersPage - 1) * customersPerPage;
  const end = customersPage * customersPerPage;
  const pageSlice = customers.slice(start, end);

  const showBulk = !!bulkSelection;
  const getRowKey = bulkSelection?.getRowKey;
  const selectedKeys = bulkSelection?.selectedKeys ?? [];
  const interactionsEnabled = bulkSelection?.interactionsEnabled ?? false;
  const isLocked = bulkSelection?.isLocked ?? false;

  const selectableRowsOnPage = showBulk
    ? pageSlice.filter((c) => bulkSelection.isRowSelectable(c))
    : [];
  const allRowsOnPageSelected =
    showBulk &&
    getRowKey &&
    selectableRowsOnPage.length > 0 &&
    selectableRowsOnPage.every((c) => selectedKeys.includes(getRowKey(c)));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <>
      {getTotalCustomers() > 0 && (
        <div
          className="card shadow-sm p-3"
          style={{
            marginTop: '33px',
            width: isCampaignSaved || showCallGroupColumns ? '100%' : '75%',
          }}
        >
          {showBulk && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h2 className="mb-0">{showCallGroupColumns ? 'Call group contacts' : 'Customers Info'}</h2>
              <div className="d-flex flex-column align-items-end gap-1">
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={bulkSelection.deleteDisabled}
                  title="Delete"
                  aria-label="Delete"
                  onClick={() => {
                    if (isLocked || !interactionsEnabled || selectedKeys.length === 0) return;
                    bulkSelection.onBulkDeleteClick();
                  }}
                  style={{
                    border: '1px solid #dc3545',
                    borderRadius: '5px',
                    padding: '8px 10px',
                    minWidth: '38px',
                    fontSize: '16px',
                    lineHeight: 1,
                    background: 'none',
                    color: bulkSelection.deleteDisabled ? 'rgba(220, 53, 69, 0.4)' : '#dc3545',
                    cursor: bulkSelection.deleteDisabled ? 'not-allowed' : 'pointer',
                    filter: bulkSelection.deleteDisabled ? 'blur(0.5px)' : 'none',
                  }}
                >
                  <i className="fa fa-trash" style={{ fontSize: '25px', margin: '-5px 0px 0px 3px' }} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
          <div
            className="table-responsive campaign-listing-table"
            style={{ height: 'auto', overflowX: 'auto', overflowY: 'visible' }}
          >
            <table className="table table-hover" style={{ minWidth: '100%', width: '100%' }}>
              <thead>
                <tr>
                {showBulk && (
                    <th
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        width: '40px',
                      }}
                    >
                      <input
                        ref={bulkSelection.selectAllCheckboxRef}
                        type="checkbox"
                        checked={allRowsOnPageSelected}
                        onChange={() => bulkSelection.onToggleSelectAll()}
                        disabled={
                          selectableRowsOnPage.length === 0 || !interactionsEnabled
                        }
                        title={
                          !interactionsEnabled && isLocked
                            ? 'Completed campaigns cannot delete contacts'
                            : 'Select all deletable contacts on this page'
                        }
                        aria-label="Select all deletable contacts on this page"
                      />
                    </th>
                  )}
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '18%' : '45%' }}>Name</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '18%' : '40%' }}>Phone</th>
                  {showCallGroupColumns && (
                    <>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '8%', fontSize: 13 }}>
                        Priority
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '8%', fontSize: 13 }}>
                        Required
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '14%', fontSize: 13 }}>
                        Group name
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '11%', fontSize: 13 }}>
                        Escalation
                      </th>
                    </>
                  )}
                  {isCampaignSaved && <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%' }}>Date Added</th>}
                  {isCampaignSaved && (
                    <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '12%' }}>
                      {showCallGroupColumns ? 'Outcome' : 'Status'}
                    </th>
                  )}
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '12%' : '15%' }}>Action</th>
                  {/* column for caller opted out of all calling communication */}
                  {isCampaignSaved && <th style={{ verticalAlign: 'middle', textAlign: 'left', minWidth: '130px', maxWidth: '160px', lineHeight: 1.3, width: '25%', padding: '8px 4px' }}>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      Caller Opted Out of All
                    </div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      Calling Communication
                    </div>
                  </th>}
                </tr>
              </thead>
              <tbody>
                {customers.slice(start, end).map((customer, index) => (
                  <tr key={customer.id || `pending-${index}`}>
                    {showBulk && (
                      <td
                        style={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: '40px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={getRowKey ? selectedKeys.includes(getRowKey(customer)) : false}
                          onChange={() => bulkSelection.onToggleRow(customer)}
                          disabled={
                            !bulkSelection.isRowSelectable(customer) || !interactionsEnabled
                          }
                          title={
                            !interactionsEnabled && isLocked
                              ? 'Completed campaigns cannot delete contacts'
                              : !bulkSelection.isRowSelectable(customer)
                                ? 'Active customers cannot be deleted'
                                : 'Select for bulk delete'
                          }
                          aria-label={`Select ${customer.name || 'contact'} for bulk delete`}
                        />
                      </td>
                    )}
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}><strong>{customer.name}</strong></td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'left', fontFamily: 'monospace' }}>
                      {(() => {
                        const digits = String(customer.phone_number || '').replace(/\D/g, '');
                        const display = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                        return display ? `+1${display}` : customer.phone_number;
                      })()}
                    </td>
                    {showCallGroupColumns && (
                      <>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          {formatCallGroupPriorityCell(customer)}
                        </td>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          {formatCallGroupRequiredCell(customer)}
                        </td>
                        <td
                          style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}
                          title={customer.call_group_name || ''}
                        >
                          {formatCallGroupNameCell(customer)}
                        </td>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          <span
                            style={{
                              color: callGroupEscalationColor(customer.call_group_member_status),
                              fontWeight: 400,
                            }}
                          >
                            {formatCallGroupMemberStatus(customer.call_group_member_status)}
                          </span>
                        </td>
                      </>
                    )}
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      {formatDate(
                        customer.created_at ||
                          customer.date_added ||
                          customer.pending_date_added
                      )}
                    </td>}
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <span style={{
                        color: (() => {
                          const ex = String(customer.execution_status || '').toLowerCase();
                          if (ex === 'called' || ex === 'answered') return '#21ba45';
                          if (ex === 'failed') return '#dc3545';
                          return '#6c757d';
                        })(),
                          fontWeight: 400,
                      }}>
                        {formatOutcomeStatus(customer.execution_status)}
                      </span>
                    </td>}
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <span style={{ display: 'inline-flex', gap: '8px', justifyContent: 'flex-start' }}>
                      <button
                        onClick={() => viewCustomer && viewCustomer(customer)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2786fb',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                        title="View customer"
                      >
                        <i className="fa fa-eye"></i>
                      </button>
                      </span>
                    </td>
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <div
                        role="switch"
                        aria-checked={!!customer.caller_opted_out}
                        aria-label={
                          customer.caller_opted_out
                            ? 'Caller opted out of all calling communication'
                            : 'Caller can receive calls'
                        }
                        onClick={() =>
                          onOptOutChange &&
                          onOptOutChange(customer, !customer.caller_opted_out)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOptOutChange &&
                              onOptOutChange(customer, !customer.caller_opted_out);
                          }
                        }}
                        tabIndex={0}
                        style={{
                          width: 44,
                          height: 24,
                          borderRadius: 12,
                          backgroundColor: customer.caller_opted_out ? '#28a745' : '#adb5bd',
                          cursor: onOptOutChange ? 'pointer' : 'default',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          margin: 0,
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: customer.caller_opted_out ? 22 : 2,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            transition: 'left 0.2s ease',
                          }}
                        />
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Showing {start + 1}-{Math.min(end, getTotalCustomers())} of {getTotalCustomers()}
            </small>
            <Pagination
              size="mini"
              activePage={customersPage}
              totalPages={Math.ceil(getTotalCustomers() / customersPerPage)}
              onPageChange={(e, { activePage }) => setCustomersPage(activePage)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerTable;
